import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';

/**
 * Toggle switch component for settings
 */
interface ToggleSwitchProps {
  id: string;
  label: string;
  description: string;
  ariaLabel: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleSwitch({ id, label, description, ariaLabel, checked, onChange }: ToggleSwitchProps) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-gray-200 dark:border-slate-700 last:border-b-0">
      <div className="flex-1 pr-4">
        <label htmlFor={id} className="block font-medium text-gray-900 dark:text-white cursor-pointer">
          {label}
        </label>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        onClick={() => onChange(!checked)}
        className={`
          relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
          ${checked ? 'bg-primary-600' : 'bg-gray-200 dark:bg-slate-600'}
        `}
      >
        <span
          className={`
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
            transition duration-200 ease-in-out
            ${checked ? 'translate-x-5' : 'translate-x-0'}
          `}
        />
      </button>
    </div>
  );
}

/**
 * Therapist Settings Page
 * Allows therapists to configure their booking preferences
 */
export function TherapistSettingsPage() {
  const { user, isLoading, isAuthenticated } = useAuth();

  // Settings state
  const [acceptingBookings, setAcceptingBookings] = useState(true);
  const [instantBooking, setInstantBooking] = useState(false);
  const [bufferTime, setBufferTime] = useState('15');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Handle save
  const handleSave = async () => {
    setSaving(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is a therapist
  if (user.role !== 'THERAPIST') {
    // Redirect admin/super_admin to admin dashboard
    if (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') {
      return <Navigate to="/admin" replace />;
    }
    // Redirect customers to user dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Therapist Settings - Massatherapy</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Link
              to="/therapist/dashboard"
              className="inline-flex items-center text-sm text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white mb-4"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
            <p className="text-gray-600 dark:text-slate-400 mt-1">
              Manage your therapist settings and preferences.
            </p>
          </div>

          {/* Settings Cards */}
          <div className="space-y-6">
            {/* Booking Settings */}
            <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-gray-200 dark:border-slate-700 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Booking Settings
              </h2>

              {/* Accepting Bookings Toggle */}
              <ToggleSwitch
                id="accepting-bookings"
                label="Accepting Bookings"
                description="Allow clients to book appointments with you"
                ariaLabel="Available for bookings"
                checked={acceptingBookings}
                onChange={setAcceptingBookings}
              />

              {/* Instant Booking Toggle */}
              <ToggleSwitch
                id="instant-booking"
                label="Instant Booking"
                description="Auto-accept bookings without manual approval"
                ariaLabel="Instant booking"
                checked={instantBooking}
                onChange={setInstantBooking}
              />

              {/* Buffer Time Select */}
              <div className="flex items-center justify-between py-4">
                <div className="flex-1 pr-4">
                  <label
                    htmlFor="buffer-time"
                    className="block font-medium text-gray-900 dark:text-white cursor-pointer"
                  >
                    Buffer Time
                  </label>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                    Break between appointments
                  </p>
                </div>
                <select
                  id="buffer-time"
                  aria-label="Buffer time between appointments"
                  value={bufferTime}
                  onChange={(e) => setBufferTime(e.target.value)}
                  className="block w-40 px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                >
                  <option value="0">No buffer</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 hour</option>
                </select>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-gray-200 dark:border-slate-700 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                Notifications
              </h2>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Notification preferences coming soon. You will be able to configure email and SMS
                notifications for new bookings, cancellations, and reminders.
              </p>
            </div>

            {/* Save Button */}
            <div className="flex items-center justify-between">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                {saving ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  'Save Settings'
                )}
              </button>

              {saved && (
                <p className="text-green-600 dark:text-green-400 flex items-center">
                  <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Settings saved successfully!
                </p>
              )}
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="font-medium text-primary-800 dark:text-primary-200">Need Help?</h3>
                <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                  If you have questions about your settings or need assistance, please contact our
                  support team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TherapistSettingsPage;
